import { ProviderInfo } from '../models/AgentConfig';
import { AiProviderSelection } from '../models/AiProvider';

/**
 * Reconciles a persisted global provider/model selection against the currently configured providers.
 *
 * The selection is loaded from localStorage and can outlive the config that produced it: a provider may be
 * removed, or a pinned model dropped from its roster. The server now validates the global default on every
 * chat turn (#2436), so a stale value 400s *every* message until it is cleared — where the old
 * `?provider=&modelId=` query string silently ignored it. Dropping the stale part here restores that
 * fail-soft behaviour on the client without weakening the server-side gate (#2440).
 *
 * - model without a provider  -> `{}` (unrecoverable; the server 400s a provider-less model default)
 * - provider gone or unavailable -> `{}` (clear it entirely)
 * - provider ok, pinned model gone -> `{ providerId }` (keep the provider; the server resolves its default)
 * - otherwise                  -> the selection unchanged (same reference, so callers can skip a no-op dispatch)
 */
export const reconcileProviderSelection = (
    selection: AiProviderSelection,
    providers: ProviderInfo[],
): AiProviderSelection => {
    if (!selection.providerId) {
        // A lone modelId (older build / tampering) is unrecoverable: the server rejects a provider-less
        // model default ("A global default model requires a provider"), so clear it rather than 400 every
        // turn. An empty selection is a genuine no-op — return it unchanged so callers skip the dispatch.
        return selection.modelId ? {} : selection;
    }

    const provider = providers.find((p) => p.id === selection.providerId);
    // Gone from the roster, OR present but currently unavailable (creds pulled, region retuned): the roster
    // still lists unavailable providers with isAvailable:false, and the server hard-400s an unavailable
    // provider on every turn — so drop the selection in both cases.
    if (!provider?.isAvailable) {
        return {};
    }

    if (selection.modelId && !provider.models.some((m) => m.id === selection.modelId)) {
        return { providerId: selection.providerId };
    }

    return selection;
};
